import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { Eyebrow, MaskLine, Reveal } from "./Primitives";
import { waLink } from "../../lib/site";

const GOALS = ["Emagrecimento", "Hipertrofia", "Condicionamento", "Saúde e qualidade de vida"];
const MODES = ["Personal Presencial", "Consultoria Online", "Consultoria Mista"];
const TIMES = ["Manhã", "Tarde", "Noite"];

const Chips = ({ label, options, value, onChange, id }) => (
  <fieldset>
    <legend className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <motion.button type="button" whileTap={{ scale: 0.95 }} key={o} onClick={() => onChange(o)}
          data-testid={`booking-${id}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={`border px-4 py-2.5 text-sm font-semibold transition-colors ${value === o ? "border-[var(--red)] bg-[var(--red)] text-[#0a0a0c]" : "border-white/20 text-zinc-200 hover:border-white/50"}`}>
          {o}
        </motion.button>
      ))}
    </div>
  </fieldset>
);

export const Booking = ({ mode, setMode }) => {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [time, setTime] = useState("");

  const message = [
    `Olá, Carlos! Meu nome é ${name.trim() || "[seu nome]"} e gostaria de agendar uma aula experimental.`,
    mode && `Modalidade: ${mode}`,
    goal && `Objetivo: ${goal}`,
    time && `Melhor período: ${time}`,
  ].filter(Boolean).join("\n");

  const submit = (e) => {
    e.preventDefault();
    window.open(waLink(message), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="agendar" className="relative overflow-hidden bg-[var(--red)] px-5 py-24 text-[#0a0a0c] md:px-10 md:py-32" data-testid="booking-section">
      <div className="pointer-events-none absolute -right-10 top-0 select-none font-display text-[40vw] font-900 leading-none text-black/10 md:text-[28vw]" aria-hidden>CM</div>
      <div className="relative mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Eyebrow index="05" dark>Aula experimental</Eyebrow>
          <h2 className="mt-8 font-display text-6xl font-900 uppercase leading-[0.85] sm:text-7xl lg:text-[8.5rem]">
            <MaskLine inView>Seu primeiro</MaskLine>
            <MaskLine inView delay={0.1}>treino é</MaskLine>
            <MaskLine inView delay={0.2}><span className="text-transparent [-webkit-text-stroke:2px_#0a0a0c]">por aqui.</span></MaskLine>
          </h2>
          <Reveal><p className="mt-8 max-w-md text-lg font-medium leading-relaxed">Preencha em 20 segundos. Montamos a mensagem e você envia direto no WhatsApp do Carlos.</p></Reveal>
        </div>

        <Reveal className="lg:col-span-6" delay={0.1}>
          <form onSubmit={submit} className="clip-notch bg-[#0a0a0c] p-6 text-[var(--bone)] md:p-10" data-testid="booking-form">
            <label className="mb-3 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400" htmlFor="bk-name">Seu nome</label>
            <input id="bk-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Como podemos te chamar?"
              data-testid="booking-name-input"
              className="w-full border-b border-white/20 bg-transparent pb-3 font-display text-3xl font-700 uppercase outline-none placeholder:text-zinc-500 focus:border-[var(--red)]" />
            <div className="mt-8 space-y-7">
              <Chips id="mode" label="Modalidade" options={MODES} value={mode} onChange={setMode} />
              <Chips id="goal" label="Objetivo" options={GOALS} value={goal} onChange={setGoal} />
              <Chips id="time" label="Melhor período" options={TIMES} value={time} onChange={setTime} />
            </div>
            <div className="mt-8 border-l-2 border-[var(--red)] bg-white/5 p-4 font-mono text-xs leading-relaxed text-zinc-300 whitespace-pre-line" data-testid="booking-message-preview">
              {message}
            </div>
            <button type="submit" data-testid="booking-submit-whatsapp"
              className="group mt-8 flex w-full items-center justify-between bg-[var(--red)] px-6 py-5 font-display text-xl font-800 uppercase tracking-wider text-[#0a0a0c] transition-colors hover:bg-[var(--bone)]">
              <span className="flex items-center gap-3"><MessageCircle className="h-5 w-5" /> Enviar no WhatsApp</span>
              <ArrowUpRight className="h-6 w-6 transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
